/**
 * createBasics.steps.js — the beginner tutorial, as data.
 *
 * THIS IS THE FILE TO EDIT to add, remove, reorder or reword a step. Nothing
 * here knows about overlays, rectangles or event listeners; the engine reads
 * these objects and does all of that. See `../../engine/stepSchema.js` for every
 * field a step may carry.
 *
 * Two conventions worth knowing:
 *
 *   - A step that asks the user to DO something advances on the outcome, not on
 *     a Next button: `advanceOn: {type:'event'}` waits for the editor to report
 *     that the thing exists, and `capture` remembers its id so later steps can
 *     point at it.
 *   - `invalid.handle` is what happens when they do the wrong thing. It undoes
 *     the mistake and returns a sentence explaining it, so a wrong drag is a
 *     correction rather than a dead end.
 */

import { TUTORIAL_TARGETS as T } from '../../targets';
import * as ScenarioAPI from '../../../services/ScenarioServices';
import { addTagToScenario } from '../../../services/TagServices';
import content from './createBasics.content';

const C = content.steps;

// Selectors for things that only exist once the tutorial has learned an id.
const node = (key) => (ctx) => `.react-flow__node[data-id="${ctx.entities[key]}"]`;
const sourceHandle = (key) => (ctx) =>
    `.react-flow__node[data-id="${ctx.entities[key]}"] .react-flow__handle.source`;
const withId = (target, key) => (ctx) => `[data-tutorial="${target}"][data-id="${ctx.entities[key]}"]`;

/**
 * Where each situation the tutorial asks for should land, as a fraction of the
 * visible canvas. Dropping is free-form in the real editor, but the tour has to
 * know in advance where a node will be: it spotlights pairs of them and hangs a
 * tooltip off one, so a node that landed on top of its neighbour — or under the
 * tooltip the user is reading — would make the next instruction unfollowable.
 * Left to right, with the two endings stacked: start, junction, ending, ending.
 * Nothing sits in the leftmost quarter, because the palette floats over it.
 */
const DROP_SPOTS = {
    start: { x: 0.30, y: 0.45 },
    normal: { x: 0.58, y: 0.45 },
    end1: { x: 0.84, y: 0.22 },
    end2: { x: 0.84, y: 0.68 },
    isolated: { x: 0.55, y: 0.85 },
};

const CANVAS_ROUTE = '/create/:scenarioId/canvas';
const TITLE_ROUTE = '/create/:scenarioId';
const PREVIEW_ROUTE = '/create/:scenarioId/preview';

/** A plain situation: the palette's ovalNode leaves endpointType empty. */
const isPlain = (payload) => !payload?.data?.endpointType;
const isEndpoint = (kind) => (payload) => payload?.data?.endpointType === kind;

/** Wrong tile dropped: take it back out and say which one to use. */
const undoNode = (kind) => ({
    handle: (payload, ctx, actions) => {
        actions.deleteSituation?.(payload.id);
        return content.hints.wrongTile(kind);
    },
});

/** Wrong two situations joined: remove the choice and say where to drag. */
const undoEdge = {
    handle: (payload, ctx, actions) => {
        actions.deleteChoice?.(payload.id);
        return content.hints.wrongEdge;
    },
};

/** Both ends of a connection step, highlighted; only the handle is draggable. */
const connectStep = ({ id, copy, from, to, capture, prefill }) => ({
    id,
    route: CANVAS_ROUTE,
    ...copy,
    spotlight: [node(from), node(to)],
    allow: [sourceHandle(from), node(to)],
    anchor: 0,
    placement: 'top',
    advanceOn: {
        type: 'event',
        name: 'choice:added',
        when: (payload, ctx) =>
            payload.source === ctx.entities[from] && payload.target === ctx.entities[to],
        capture,
    },
    onEvent: (payload, ctx, actions) => actions.updateChoice?.(payload.id, prefill),
    invalid: undoEdge,
});

/** A palette tile dragged onto the canvas, then filled in for the user. */
const dragStep = ({ id, copy, tile, dropAt, when, capture, prefill, kind, placement = 'right' }) => ({
    id,
    route: CANVAS_ROUTE,
    ...copy,
    spotlight: [tile, T.CANVAS],
    anchor: 0,
    placement,
    sidebarPanel: 'createSituation',
    dropAt,
    advanceOn: { type: 'event', name: 'situation:added', when, capture },
    onEvent: (payload, ctx, actions) => actions.updateSituation?.(payload.id, prefill),
    invalid: undoNode(kind),
});

const steps = [
    // ── Creating the scenario ────────────────────────────────────────────────
    {
        id: 'create-modal',
        // The navbar is not on /create/*, so this step belongs to whatever page
        // the user pressed Create from.
        route: '/*',
        ...C.createModal,
        spotlight: [T.CREATE_MODAL_TITLE, T.CREATE_MODAL_CREATE_BUTTON],
        allow: [T.CREATE_MODAL_CREATE_BUTTON],
        anchor: 1,
        placement: 'bottom',
        advanceOn: { type: 'event', name: 'scenario:created', capture: 'scenarioId' },
        // Awaited by the create dialog, so the settings page below is already
        // populated the first time it renders.
        onEvent: async ({ scenarioId }, ctx) => {
            await ScenarioAPI.updateScenario(scenarioId, {
                description: content.scenario.descriptionHtml,
                isPrivate: false,
                isHidden: true,
                isLogged: false,
                showMap: false,
            });
            await addTagToScenario(scenarioId, content.scenario.tag, ctx.userId);
        },
    },

    // ── The settings page ────────────────────────────────────────────────────
    { id: 'title-title', route: TITLE_ROUTE, ...C.titleTitle, spotlight: [T.TITLE_TITLE], allow: [], advanceOn: { type: 'next' } },
    { id: 'title-description', route: TITLE_ROUTE, ...C.titleDescription, spotlight: [T.TITLE_DESCRIPTION], allow: [], placement: 'top', advanceOn: { type: 'next' } },
    { id: 'title-password', route: TITLE_ROUTE, ...C.titlePassword, spotlight: [T.TITLE_PASSWORD], allow: [], placement: 'right', advanceOn: { type: 'next' } },
    { id: 'title-hidden', route: TITLE_ROUTE, ...C.titleHidden, spotlight: [T.TITLE_HIDDEN], allow: [], placement: 'right', advanceOn: { type: 'next' } },
    { id: 'title-logs', route: TITLE_ROUTE, ...C.titleLogs, spotlight: [T.TITLE_LOGS], allow: [], placement: 'right', advanceOn: { type: 'next' } },
    { id: 'title-map', route: TITLE_ROUTE, ...C.titleMap, spotlight: [T.TITLE_MAP], allow: [], placement: 'right', advanceOn: { type: 'next' } },
    { id: 'title-collaborators', route: TITLE_ROUTE, ...C.titleCollaborators, spotlight: [T.TITLE_COLLABORATORS], allow: [], placement: 'right', advanceOn: { type: 'next' } },
    { id: 'title-tags', route: TITLE_ROUTE, ...C.titleTags, spotlight: [T.TITLE_TAGS], allow: [], placement: 'top', advanceOn: { type: 'next' } },
    {
        id: 'title-next',
        route: TITLE_ROUTE,
        ...C.titleNext,
        spotlight: [T.TITLE_NEXT],
        placement: 'top',
        // Saving the settings is a round trip; wait for the editor, not the click.
        advanceOn: { type: 'route', path: CANVAS_ROUTE },
    },

    // ── First situation ──────────────────────────────────────────────────────
    {
        id: 'open-create-panel',
        route: CANVAS_ROUTE,
        ...C.openCreatePanel,
        // The palette is already out: the editor opens with it. So this step
        // points at it rather than asking for a click, which on an open panel
        // would toggle it shut.
        spotlight: [T.RAIL_CREATE_SITUATION, T.PALETTE_PANEL],
        allow: [],
        anchor: 1,
        placement: 'right',
        sidebarPanel: 'createSituation',
        advanceOn: { type: 'next' },
    },
    dragStep({
        id: 'drag-start',
        copy: C.dragStart,
        tile: T.PALETTE_START,
        dropAt: DROP_SPOTS.start,
        when: isEndpoint('start'),
        capture: 'startNodeId',
        prefill: content.nodes.start,
        kind: 'START',
    }),
    { id: 'edit-title', route: CANVAS_ROUTE, ...C.editTitle, spotlight: [T.EDIT_SITUATION_TITLE], allow: [], placement: 'left', advanceOn: { type: 'next' } },
    { id: 'edit-text', route: CANVAS_ROUTE, ...C.editText, spotlight: [T.EDIT_SITUATION_TEXT], allow: [], placement: 'left', advanceOn: { type: 'next' } },
    {
        id: 'open-preview-1',
        route: CANVAS_ROUTE,
        ...C.openPreview1,
        spotlight: [T.EDIT_SITUATION_PREVIEW],
        placement: 'left',
        advanceOn: { type: 'route', path: PREVIEW_ROUTE },
    },
    { id: 'preview-text', route: PREVIEW_ROUTE, ...C.previewText, spotlight: [T.PREVIEW_SITUATION_TEXT], allow: [], placement: 'bottom', advanceOn: { type: 'next' } },
    {
        id: 'preview-exit-1',
        route: PREVIEW_ROUTE,
        ...C.previewExit1,
        spotlight: [T.PREVIEW_EXIT],
        placement: 'bottom',
        advanceOn: { type: 'route', path: CANVAS_ROUTE },
    },

    // ── Second situation and the first choice ────────────────────────────────
    dragStep({
        id: 'drag-normal',
        copy: C.dragNormal,
        tile: T.PALETTE_SITUATION,
        dropAt: DROP_SPOTS.normal,
        when: isPlain,
        capture: 'normalNodeId',
        prefill: content.nodes.normal,
        kind: 'plain situation',
    }),
    connectStep({
        id: 'connect-start-normal',
        copy: C.connectStartNormal,
        from: 'startNodeId',
        to: 'normalNodeId',
        capture: 'choice1Id',
        prefill: content.choices.startToNormal,
    }),
    {
        id: 'choice-explain',
        route: CANVAS_ROUTE,
        ...C.choiceExplain,
        spotlight: [T.EDIT_CHOICE_TITLE, T.EDIT_CHOICE_TEXT],
        allow: [],
        anchor: 1,
        placement: 'left',
        advanceOn: { type: 'next' },
    },
    {
        id: 'select-start-node',
        route: CANVAS_ROUTE,
        ...C.selectStart,
        spotlight: [node('startNodeId')],
        // Clicking the node selects it; clicking its handle starts a connection.
        deny: ['.react-flow__handle'],
        placement: 'top',
        advanceOn: { type: 'click', target: node('startNodeId') },
    },
    {
        id: 'open-preview-2',
        route: CANVAS_ROUTE,
        ...C.openPreview2,
        spotlight: [T.EDIT_SITUATION_PREVIEW],
        placement: 'left',
        advanceOn: { type: 'route', path: PREVIEW_ROUTE },
    },
    {
        id: 'preview-choice',
        route: PREVIEW_ROUTE,
        ...C.previewChoice,
        spotlight: [withId(T.PREVIEW_CHOICE, 'choice1Id')],
        allow: [],
        placement: 'bottom',
        advanceOn: { type: 'next' },
    },
    {
        id: 'preview-exit-2',
        route: PREVIEW_ROUTE,
        ...C.previewExit2,
        spotlight: [T.PREVIEW_EXIT],
        placement: 'bottom',
        advanceOn: { type: 'route', path: CANVAS_ROUTE },
    },

    // ── Two endings, two branches ────────────────────────────────────────────
    dragStep({
        id: 'drag-end-1',
        copy: C.dragEnd1,
        tile: T.PALETTE_END,
        dropAt: DROP_SPOTS.end1,
        when: isEndpoint('end'),
        capture: 'end1Id',
        prefill: content.nodes.end1,
        kind: 'END',
    }),
    dragStep({
        id: 'drag-end-2',
        copy: C.dragEnd2,
        tile: T.PALETTE_END,
        dropAt: DROP_SPOTS.end2,
        when: isEndpoint('end'),
        capture: 'end2Id',
        prefill: content.nodes.end2,
        kind: 'END',
    }),
    connectStep({
        id: 'connect-normal-end1',
        copy: C.connectNormalEnd1,
        from: 'normalNodeId',
        to: 'end1Id',
        capture: 'choice2Id',
        prefill: content.choices.normalToEnd1,
    }),
    connectStep({
        id: 'connect-normal-end2',
        copy: C.connectNormalEnd2,
        from: 'normalNodeId',
        to: 'end2Id',
        capture: 'choice3Id',
        prefill: content.choices.normalToEnd2,
    }),

    // ── Reading it end to end ────────────────────────────────────────────────
    {
        id: 'rail-preview',
        route: CANVAS_ROUTE,
        ...C.railPreview,
        spotlight: [T.RAIL_PREVIEW],
        placement: 'right',
        advanceOn: { type: 'route', path: PREVIEW_ROUTE },
    },
    { id: 'preview-start', route: PREVIEW_ROUTE, ...C.previewStart, spotlight: [T.PREVIEW_START], placement: 'top', advanceOn: { type: 'click', target: T.PREVIEW_START } },
    {
        id: 'preview-choice-1',
        route: PREVIEW_ROUTE,
        ...C.previewChoice1,
        spotlight: [withId(T.PREVIEW_CHOICE, 'choice1Id')],
        placement: 'bottom',
        advanceOn: { type: 'click', target: withId(T.PREVIEW_CHOICE, 'choice1Id') },
    },
    {
        id: 'preview-end-1',
        route: PREVIEW_ROUTE,
        ...C.previewEnd1,
        spotlight: [withId(T.PREVIEW_CHOICE, 'choice2Id')],
        placement: 'bottom',
        advanceOn: { type: 'click', target: withId(T.PREVIEW_CHOICE, 'choice2Id') },
    },
    { id: 'preview-restart', route: PREVIEW_ROUTE, ...C.previewRestart, spotlight: [T.PREVIEW_RESTART], placement: 'bottom', advanceOn: { type: 'click', target: T.PREVIEW_RESTART } },
    { id: 'preview-start-2', route: PREVIEW_ROUTE, ...C.previewStart2, spotlight: [T.PREVIEW_START], placement: 'top', advanceOn: { type: 'click', target: T.PREVIEW_START } },
    {
        id: 'preview-choice-1b',
        route: PREVIEW_ROUTE,
        ...C.previewChoice1b,
        spotlight: [withId(T.PREVIEW_CHOICE, 'choice1Id')],
        placement: 'bottom',
        advanceOn: { type: 'click', target: withId(T.PREVIEW_CHOICE, 'choice1Id') },
    },
    {
        id: 'preview-end-2',
        route: PREVIEW_ROUTE,
        ...C.previewEnd2,
        spotlight: [withId(T.PREVIEW_CHOICE, 'choice3Id')],
        placement: 'bottom',
        advanceOn: { type: 'click', target: withId(T.PREVIEW_CHOICE, 'choice3Id') },
    },
    {
        id: 'preview-exit-3',
        route: PREVIEW_ROUTE,
        ...C.previewExit3,
        spotlight: [T.PREVIEW_EXIT],
        placement: 'bottom',
        advanceOn: { type: 'route', path: CANVAS_ROUTE },
    },

    // ── Breaking it, finding it, fixing it ───────────────────────────────────
    dragStep({
        id: 'drag-isolated',
        copy: C.dragIsolated,
        tile: T.PALETTE_SITUATION,
        dropAt: DROP_SPOTS.isolated,
        when: isPlain,
        capture: 'isolatedNodeId',
        prefill: content.nodes.isolated,
        kind: 'plain situation',
    }),
    {
        id: 'check-validity-1',
        route: CANVAS_ROUTE,
        ...C.checkValidity1,
        spotlight: [T.RAIL_CHECK_VALIDITY],
        placement: 'right',
        advanceOn: { type: 'click', target: T.RAIL_CHECK_VALIDITY },
    },
    {
        id: 'click-error-item',
        route: CANVAS_ROUTE,
        ...C.clickErrorItem,
        spotlight: [withId(T.ERROR_ITEM, 'isolatedNodeId')],
        placement: 'top',
        advanceOn: { type: 'click', target: withId(T.ERROR_ITEM, 'isolatedNodeId') },
    },
    {
        id: 'open-settings-tab',
        route: CANVAS_ROUTE,
        ...C.openSettingsTab,
        spotlight: [{ selector: `[data-tutorial="${T.EDIT_TAB}"][data-id="3"]` }],
        placement: 'bottom',
        advanceOn: { type: 'click', target: { selector: `[data-tutorial="${T.EDIT_TAB}"][data-id="3"]` } },
    },
    {
        id: 'click-delete',
        route: CANVAS_ROUTE,
        ...C.clickDelete,
        spotlight: [T.EDIT_SITUATION_DELETE],
        placement: 'top',
        advanceOn: { type: 'click', target: T.EDIT_SITUATION_DELETE },
    },
    {
        id: 'confirm-delete',
        route: CANVAS_ROUTE,
        ...C.confirmDelete,
        spotlight: [T.DELETE_MODAL_YES],
        placement: 'bottom',
        advanceOn: {
            type: 'event',
            name: 'situation:removed',
            when: (payload, ctx) => payload.id === ctx.entities.isolatedNodeId,
        },
    },
    {
        id: 'check-validity-2',
        route: CANVAS_ROUTE,
        ...C.checkValidity2,
        spotlight: [T.RAIL_CHECK_VALIDITY],
        placement: 'right',
        advanceOn: { type: 'click', target: T.RAIL_CHECK_VALIDITY },
    },
    {
        id: 'publish',
        route: CANVAS_ROUTE,
        ...C.publish,
        spotlight: [T.RAIL_PUBLISH],
        placement: 'right',
        advanceOn: { type: 'event', name: 'scenario:published' },
        // Publishing validates server-side too; if it refuses, say so rather
        // than leaving the user clicking a button that appears to do nothing.
        hintOn: { 'scenario:publishFailed': content.hints.publishFailed },
    },
];

export default steps;
